import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Room as RoomIcon,
  MyLocation as MyLocationIcon,
  Directions as DirectionsIcon,
  Map as MapIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import TableRowSkeleton from "../TableRowSkeleton";
import RouteModal from "../RouteModal";
import MapSelectionModal from "../MapSelectionModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SP","SE","TO"];

// Aceita o CNAE do jeito que as pessoas normalmente escrevem (com pontos
// e barra, ex: "4711-3/02") e reduz pra so os digitos - a CNPJa so
// aceita numero puro em activities.id.in (erro real reportado pelo
// usuario 2026-09-16, "deu erro ao buscar o cnae": digitou com
// pontuação e a API rejeitava com 400 sem explicação nenhuma na tela).
const sanitizeActivityIds = (raw) =>
  raw
    .split(",")
    .map((part) => part.replace(/\D/g, ""))
    .filter(Boolean)
    .join(",");

const formatPhone = (p) => (p ? `(${p.area}) ${p.number}` : "");
const formatCnpj = (taxId) =>
  taxId ? taxId.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : "";
const formatCurrency = (value) =>
  typeof value === "number"
    ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
    : "";

// A Receita Federal so' divulga publicamente SE a empresa optou pelo
// Simples/MEI ou nao - nao divulga se quem nao optou e' Lucro Real ou
// Presumido (esse dado nao e' publico). Rotulo honesto em vez de
// inventar "Lucro Real" pra quem so' sabemos que "nao e' optante" -
// pedido do usuario 2026-09-16 pediu especificamente ver o regime, mas
// mostrar errado seria pior que nao mostrar.
const regimeLabel = (company) => {
  if (company?.simei?.optant) return "MEI";
  if (company?.simples?.optant) return "Simples Nacional";
  return "Não optante (Lucro Real/Presumido)";
};

const buildAddressLine = (address) =>
  address
    ? [
        [address.street, address.number].filter(Boolean).join(", "),
        address.details,
        address.district,
        `${address.city}/${address.state}`,
        address.zip,
      ]
        .filter(Boolean)
        .join(" - ")
    : "";

// Mapa GRATUITO - abre o Google Maps com o endereco em texto (o Google
// geolocaliza sozinho quando a pessoa clica, sem gastar nenhum credito
// da CNPJa). Pedido do usuario 2026-09-16 ("tem como criar as duas
// opcoes e o usuario seleciona").
const freeMapUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(buildAddressLine(address))}`;

// Cadastra o lead como Contato de verdade (nao so' texto no card) - sem
// isso nao tinha como iniciar uma conversa de WhatsApp com ele. Pedido
// do usuario 2026-09-16 ("preciso que as prospeccoes precise cadastrar
// o cliente pois assim consigo enviar mensagem"). Reaproveita o mesmo
// POST /contacts que o resto do sistema usa - o backend ja valida se o
// numero existe de verdade no WhatsApp (CheckContactNumber), entao um
// telefone fixo ou numero invalido simplesmente nao vira contato, sem
// quebrar a criacao do card avulso (tentativa best-effort, nunca
// bloqueia o resto do fluxo).
const tryCreateContact = async (name, phoneDigits) => {
  if (!phoneDigits) return { created: false, reason: "no-phone" };
  try {
    const { data } = await api.post("/contacts", { name, number: phoneDigits });
    return { created: true, contactId: data?.id };
  } catch (err) {
    const reason = err?.response?.data?.error?.includes("whatsapp") ? "not-whatsapp" : "unknown";
    return { created: false, reason };
  }
};

// Aba "Buscar empresas" da tela Comercial (frontend/src/pages/Commercial)
// - antes era uma pagina separada (/company-search), unificada aqui a
// pedido do usuario 2026-09-16 ("isso deveria estar junto da tela de
// prospeccao e isso alimentar o kanban", junto com o Painel na mesma
// tela). boardId/firstPhaseId/customFieldDefinitions vem do board
// selecionado no topo da pagina pai - essa aba nao escolhe board sozinha.
// Busca empresas por CNAE + UF + data de abertura na API Comercial da
// CNPJa (GET /company-search, que proxya pra GET /office na CNPJa - ver
// SearchCompaniesService no backend) e permite adicionar o resultado
// direto como card avulso no board (reaproveita os
// CustomFieldDefinitions que ja existem la: Empresa/CNPJ/Telefone/Fonte
// de Indicação).
const CompanySearchPanel = ({ boardId, firstPhaseId, customFieldDefinitions, onAdded, allowPaidGeocoding }) => {
  const [activityIds, setActivityIds] = useState("");
  const [states, setStates] = useState([]);
  const [foundedGte, setFoundedGte] = useState("");
  const [foundedLte, setFoundedLte] = useState("");
  const [onlyWithPhone, setOnlyWithPhone] = useState(false);
  const [onlyWithEmail, setOnlyWithEmail] = useState(true);
  const [cityFilter, setCityFilter] = useState("");
  // Bairro - pedido do usuario 2026-09-28 ("preciso que no campo de
  // pesquisa eu possa digitar o nome do bairro"). A API da CNPJa so'
  // filtra por cidade (codigo IBGE de municipio, ver ResolveMunicipalityService)
  // - nao ha' um filtro de bairro do lado de la', e nomes de bairro nao
  // sao um codigo padronizado como municipio pra arriscar mandar como
  // parametro novo pra uma API paga (credito real por busca). Filtra no
  // que ja veio da pagina atual (campo "district" do endereco da CNPJa,
  // ver buildAddressLine) em vez de fazer outra chamada.
  const [bairroFilter, setBairroFilter] = useState("");

  const [results, setResults] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingTaxId, setAddingTaxId] = useState(null);
  const [addedTaxIds, setAddedTaxIds] = useState([]);
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const [geocodingTaxId, setGeocodingTaxId] = useState(null);
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const estimatedCredits = Math.ceil(50 / 10);

  const runSearch = useCallback(
    async (token) => {
      setLoading(true);
      try {
        const { data } = await api.get("/company-search", {
          params: {
            activityIds: sanitizeActivityIds(activityIds) || undefined,
            states: states.length ? states.join(",") : undefined,
            // Cidade agora e' filtro de verdade na API (resolvido pro
            // codigo IBGE do municipio no backend), nao mais um filtro
            // local em cima dos 50 primeiros resultados - bug real
            // reportado pelo usuario 2026-09-16 ("pesquisei empresas da
            // cidade poa e nao trouxe nenhum resultado").
            city: cityFilter.trim() || undefined,
            foundedGte: foundedGte || undefined,
            foundedLte: foundedLte || undefined,
            onlyWithPhone: onlyWithPhone || undefined,
            onlyWithEmail: onlyWithEmail || undefined,
            token: token || undefined,
            limit: 50,
          },
        });
        setResults((prev) => (token ? [...prev, ...data.records] : data.records));
        setNextToken(data.next || null);
        setTotalCount(data.count);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [activityIds, states, foundedGte, foundedLte, onlyWithPhone, onlyWithEmail, cityFilter]
  );

  const handleSearch = () => {
    setResults([]);
    setNextToken(null);
    setSelectedTaxIds([]);
    runSearch(null);
  };

  const filteredResults = useMemo(() => {
    const term = bairroFilter.trim().toLowerCase();
    if (!term) return results;
    return results.filter((r) => (r.address?.district || "").toLowerCase().includes(term));
  }, [results, bairroFilter]);

  const toggleSelected = (taxId) => {
    setSelectedTaxIds((prev) =>
      prev.includes(taxId) ? prev.filter((id) => id !== taxId) : [...prev, taxId]
    );
  };

  // Geolocalizacao PAGA (credito real da CNPJa) - so' aparece se o admin
  // ligou o toggle nas configuracoes (allowPaidGeocoding). Mais precisa
  // que o link gratuito porque usa o endereco oficial da Receita
  // Federal em vez de depender do Google achar o endereco certo.
  const handlePaidGeocode = async (record) => {
    setGeocodingTaxId(record.taxId);
    try {
      const { data } = await api.get(`/company-search/${record.taxId}/geocode`);
      window.open(`https://www.google.com/maps?q=${data.latitude},${data.longitude}`, "_blank", "noopener,noreferrer");
    } catch (err) {
      toastError(err);
    } finally {
      setGeocodingTaxId(null);
    }
  };

  const getFieldId = (name) =>
    (customFieldDefinitions || []).find((d) => d.name.toLowerCase() === name.toLowerCase())?.id;

  const handleAddToKanban = async (record) => {
    if (!boardId || !firstPhaseId) {
      toast.error("Esse board ainda não tem fases configuradas.");
      return;
    }
    setAddingTaxId(record.taxId);
    try {
      const title = record.alias || record.company?.name || formatCnpj(record.taxId);
      const { data: card } = await api.post("/cards", {
        boardId,
        phaseId: firstPhaseId,
        title,
      });

      const addressLine = buildAddressLine(record.address);

      const sociosLine = (record.company?.members || [])
        .map((m) => `${m.person?.name}${m.role?.text ? ` (${m.role.text})` : ""}`)
        .join("; ");

      // Todos os campos que a busca CNPJa consegue extrair, pra ja
      // alimentar o card sem precisar digitar de novo - pedido do
      // usuario 2026-09-16 ("essas informações do lead precisam
      // alimentar o kanban... incluindo endereço"). Os campos
      // (Endereço/Sócios/Regime Tributário/Porte/Capital Social) foram
      // criados no board Comercial junto com essa mudanca.
      const fieldValues = [
        { name: "Empresa", value: record.company?.name || record.alias || "" },
        { name: "CNPJ", value: formatCnpj(record.taxId) },
        { name: "Telefone", value: record.phones?.[0] ? formatPhone(record.phones[0]) : "" },
        { name: "Fonte de Indicação", value: "Prospecção (CNPJá)" },
        { name: "Endereço", value: addressLine },
        { name: "Sócios", value: sociosLine },
        { name: "Regime Tributário", value: regimeLabel(record.company) },
        { name: "Porte", value: record.company?.size?.text || "" },
        { name: "Capital Social", value: formatCurrency(record.company?.equity) },
        {
          name: "Observação",
          value: [
            record.mainActivity?.text,
            record.emails?.[0]?.address,
          ]
            .filter(Boolean)
            .join(" · "),
        },
      ];

      await Promise.all(
        fieldValues
          .filter((f) => f.value && getFieldId(f.name))
          .map((f) =>
            api.post("/card-field-values", {
              cardId: card.id,
              customFieldDefinitionId: getFieldId(f.name),
              value: f.value,
            })
          )
      );

      // Tenta cadastrar o telefone como Contato de verdade (best-effort,
      // depois do card ja estar criado - se falhar, o card continua
      // valido, so' nao da' pra mandar mensagem direto ainda).
      const phone = record.phones?.[0];
      const phoneDigits = phone ? `55${phone.area}${phone.number}` : null;
      const contactResult = await tryCreateContact(title, phoneDigits);

      // Vincula o Contato criado direto no card - sem isso o botao
      // "Agendar mensagem"/"Agendar compromisso" do Kanban nao acha
      // ninguem pra pre-selecionar em cards avulsos (sem ticket) e pede
      // pra buscar manualmente, mesmo o contato ja existindo. Pedido do
      // usuario 2026-09-18.
      if (contactResult.created && contactResult.contactId) {
        await api.put(`/cards/${card.id}`, { contactId: contactResult.contactId });
      }

      setAddedTaxIds((prev) => [...prev, record.taxId]);
      if (onAdded) {
        onAdded(contactResult);
      } else if (contactResult.created) {
        toast.success("Empresa adicionada ao Kanban e cliente cadastrado!");
      } else if (contactResult.reason === "no-phone") {
        // Antes dava a mesma mensagem de sucesso mesmo sem telefone
        // nenhum - o usuario so' descobria que nao dava pra mandar
        // mensagem depois, sem saber por que. Pedido 2026-09-28 ("nas
        // pesquisas quando cria as leads nao esta trazendo o numero de
        // telefone") - a CNPJa as vezes simplesmente nao tem telefone
        // cadastrado pra empresa (nao e' bug daqui), mas agora fica
        // claro na hora, pra completar manualmente se precisar mandar
        // mensagem.
        toast.warn("Empresa adicionada, mas a CNPJá não trouxe telefone - adicione manualmente pra poder mandar mensagem.");
      } else if (contactResult.reason === "not-whatsapp") {
        toast.warn("Empresa adicionada, mas o telefone encontrado não tem WhatsApp - confira/corrija manualmente pra poder mandar mensagem.");
      } else {
        toast.success("Empresa adicionada ao Kanban!");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setAddingTaxId(null);
    }
  };

  return (
    <Box>
      <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="CNAEs (separados por vírgula)"
              placeholder="Ex: 4711-3/02, 5611-2/03"
              helperText="Com ou sem pontuação, tanto faz"
              size="small"
              fullWidth
              variant="outlined"
              value={activityIds}
              onChange={(e) => setActivityIds(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              select
              label="UF (região)"
              size="small"
              fullWidth
              variant="outlined"
              SelectProps={{
                multiple: true,
                value: states,
                onChange: (e) => setStates(e.target.value),
                renderValue: (selected) => selected.join(", "),
              }}
            >
              {UFS.map((uf) => (
                <MenuItem key={uf} value={uf}>
                  <Checkbox checked={states.indexOf(uf) > -1} size="small" />
                  <ListItemText primary={uf} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Cidade"
              size="small"
              fullWidth
              variant="outlined"
              helperText="Precisa selecionar a UF também"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Bairro"
              size="small"
              fullWidth
              variant="outlined"
              helperText="Filtra os resultados já trazidos, não busca de novo"
              value={bairroFilter}
              onChange={(e) => setBairroFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Aberta a partir de"
              type="date"
              size="small"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={foundedGte}
              onChange={(e) => setFoundedGte(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Aberta até"
              type="date"
              size="small"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={foundedLte}
              onChange={(e) => setFoundedLte(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Box display="flex" alignItems="center" height="100%" gridGap={16}>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={onlyWithPhone}
                  onChange={(e) => setOnlyWithPhone(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">Só com telefone</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={onlyWithEmail}
                  onChange={(e) => setOnlyWithEmail(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">Só com e-mail</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSearch}
              disabled={loading}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
          Cada busca consome créditos reais da CNPJá (1 crédito a cada 10 resultados retornados). Página atual: até 50
          resultados (~{estimatedCredits} créditos).
        </Typography>
      </Paper>

      <Paper variant="outlined" style={{ padding: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">
            {bairroFilter.trim()
              ? `${filteredResults.length} de ${results.length} (filtro de bairro)`
              : totalCount !== null
              ? `${results.length} de ${totalCount} encontrados`
              : "Resultados"}
          </Typography>
          <Box display="flex" gridGap={8}>
            {selectedTaxIds.length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<DirectionsIcon />}
                onClick={() => setRouteModalOpen(true)}
              >
                Rotear {selectedTaxIds.length} selecionados
              </Button>
            )}
            {filteredResults.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<MapIcon />}
                onClick={() => setMapModalOpen(true)}
              >
                Ver todos no mapa
              </Button>
            )}
          </Box>
        </Box>
        <Box style={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Empresa</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Sócios</TableCell>
              <TableCell>Atividade</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Abertura</TableCell>
              <TableCell align="center">Mapa</TableCell>
              <TableCell align="center">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && results.length === 0 ? (
              <TableRowSkeleton columns={10} />
            ) : (
              filteredResults.map((r) => {
                const added = addedTaxIds.includes(r.taxId);
                const members = r.company?.members || [];
                const selected = selectedTaxIds.includes(r.taxId);
                return (
                  <TableRow key={r.taxId} selected={selected}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selected} onChange={() => toggleSelected(r.taxId)} />
                    </TableCell>
                    <TableCell>
                      <b>{r.alias || r.company?.name}</b>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {[r.company?.size?.text, regimeLabel(r.company), formatCurrency(r.company?.equity)]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatCnpj(r.taxId)}</TableCell>
                    <TableCell>
                      {members.length > 0 && (
                        <Tooltip
                          title={members.map((m) => `${m.person?.name}${m.role?.text ? ` — ${m.role.text}` : ""}`).join(" | ")}
                        >
                          <Chip size="small" icon={<PeopleIcon />} label={members.length} variant="outlined" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell style={{ maxWidth: 220 }}>{r.mainActivity?.text}</TableCell>
                    <TableCell>
                      {r.address?.city}/{r.address?.state}
                    </TableCell>
                    <TableCell>
                      {r.phones?.[0] && (
                        <Box display="flex" alignItems="center">
                          <PhoneIcon fontSize="inherit" style={{ marginRight: 4 }} />
                          {formatPhone(r.phones[0])}
                        </Box>
                      )}
                      {r.emails?.[0] && (
                        <Box display="flex" alignItems="center">
                          <EmailIcon fontSize="inherit" style={{ marginRight: 4 }} />
                          <span style={{ fontSize: "0.8em" }}>{r.emails[0].address}</span>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{r.founded}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver no mapa (grátis)">
                        <IconButton
                          size="small"
                          component="a"
                          href={freeMapUrl(r.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <RoomIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {allowPaidGeocoding && (
                        <Tooltip title="Geolocalizar exato (CNPJá, paga 1 crédito)">
                          <IconButton
                            size="small"
                            onClick={() => handlePaidGeocode(r)}
                            disabled={geocodingTaxId === r.taxId}
                          >
                            <MyLocationIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {added ? (
                        <Chip size="small" label="No Kanban" color="primary" variant="outlined" />
                      ) : (
                        <IconButton
                          size="small"
                          title="Adicionar ao Kanban"
                          onClick={() => handleAddToKanban(r)}
                          disabled={addingTaxId === r.taxId}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {!loading && results.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Chip icon={<BusinessIcon />} label="Nenhuma busca feita ainda ou nenhum resultado" />
                </TableCell>
              </TableRow>
            )}
            {!loading && results.length > 0 && filteredResults.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Chip icon={<BusinessIcon />} label={`Nenhum resultado com bairro "${bairroFilter}"`} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
        {nextToken && (
          <Box textAlign="center" mt={2}>
            <Button onClick={() => runSearch(nextToken)} disabled={loading}>
              {loading ? "Carregando..." : "Carregar mais"}
            </Button>
          </Box>
        )}
      </Paper>

      {routeModalOpen && (
        <RouteModal
          open={routeModalOpen}
          onClose={() => setRouteModalOpen(false)}
          boardId={boardId}
          points={results
            .filter((r) => selectedTaxIds.includes(r.taxId))
            .map((r) => ({
              label: r.alias || r.company?.name || formatCnpj(r.taxId),
              address: buildAddressLine(r.address),
            }))}
        />
      )}

      {mapModalOpen && (
        <MapSelectionModal
          open={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
          boardId={boardId}
          points={filteredResults
            .map((r) => ({
              label: r.alias || r.company?.name || formatCnpj(r.taxId),
              address: buildAddressLine(r.address),
            }))
            .filter((p) => p.address)}
        />
      )}
    </Box>
  );
};

export default CompanySearchPanel;
